import { type FC } from 'react'
import { useTranslation } from 'react-i18next'

import { InlineWarningTooltip } from '@/components/page/Items/ItemControlOverlay/controls/InlineWarningTooltip'
import { LowResolutionWarning } from '@/components/page/Items/ItemControlOverlay/controls/LowResolutionWarning'
import type { BleedWarningState, ImageControlState } from '@/types/itemControls'

type WarningIndicatorsProps = {
  /** The item the warnings belong to; the PPI warning resolves its photo. */
  itemId: string
  itemType: string
  imageState?: ImageControlState
  warnings?: BleedWarningState
}

/**
 * Warning indicators for items (rendered in WarningBar on right side):
 * - PPI quality warning (images only), whose tooltip offers "Enhance image
 *   size" when the item shows a case photo
 * - Bleed/margin overlap warning (Lotus editor text boxes only)
 *
 * A text box's load result (`warnings.loadWarningMessage`) paints nothing
 * here: by design a text box shows the margin-or-fold overlap icon
 * and, for a Lotus box, the red "Text doesn't fit" band its own styles paint
 * under an overflowing box (`PageEditorTextBox.styled.ts`), and no other
 * warning. The result is still computed, stamped into the control state and
 * logged (`useBleedWarning`, `textBoxLoadLog.ts`).
 */
export const WarningIndicators: FC<WarningIndicatorsProps> = ({
  itemId,
  itemType,
  imageState,
  warnings,
}) => {
  const { t } = useTranslation()

  const isImageItem = itemType === 'image'

  const showPpiWarning =
    isImageItem && imageState?.showPrintQualityWarning === true
  const showBleedWarning = warnings?.showBleedWarning === true

  if (!showPpiWarning && !showBleedWarning) {
    return null
  }

  const effectivePixelsPerInch = imageState?.effectivePixelsPerInch

  return (
    <>
      {showPpiWarning && effectivePixelsPerInch != null && (
        <LowResolutionWarning
          itemId={itemId}
          effectivePixelsPerInch={effectivePixelsPerInch}
          originalImagePixelSize={imageState?.originalImagePixelSize}
        />
      )}

      {showBleedWarning && warnings?.bleedWarningMessage && (
        <InlineWarningTooltip
          message={warnings.bleedWarningMessage}
          width="120px"
          ariaLabel={t('toast.bleedWarningAriaLabel')}
        />
      )}
    </>
  )
}
